import styled from '@emotion/styled';
import { useUpdateEffect, useSize } from 'ahooks';
import { Tabs, Tooltip } from 'antd';
import Empty from 'components/Empty';
import { initEcharts, EChartsOption, applyChartTheme } from 'lib/echarts';
import dayjs from 'lib/dayjs';
import { useEffect, useRef, useState } from 'react';
import { useTranslate, TFnType } from '@tolgee/react';
import useDrawerContext from '../Trend/hooks/useDrawer';
import { useDashboardFilterContext, useDashboardPlatformInfo } from '../Filter/useFilter';
import { What } from 'views/Marketing/components/PolicyTable';
import { numberAbs, numberNegative } from '../../utils';

const Container = styled.div`
  margin-top: 16px;
  border-radius: 4px;
  height: 440px;
  width: 100%;
  background: var(--fett-bg-panel);
  .ant-tabs-nav-wrap {
    height: 56px;
  }
`;

const Relative = styled.div`
  position: relative;
  width: 100%;
  padding-top: 24px;
  .chart {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    height: 390px;
    position: absolute;
    left: 50%;
    top: 0;
  }
  .el {
    height: 346px;
    width: 100%;
  }
`;
interface Props {
  value?: Dashboard.OrderOverviewInner;
}
const convert2CODOptions = (value: Dashboard.OrderOverviewInner, t: TFnType): EChartsOption => {
  const data = value.COD每日盈利 || [];
  const dateRange = data.map((x) => x.date).map((x) => dayjs(x).format('MM-DD'));
  return {
    grid: {
      left: '6%',
      right: '6%',
    },
    legend: {
      data: [
        {
          name: t('ViewDashboard.下单总量'),
          itemStyle: {
            color: '#5b8ff9',
          },
          lineStyle: {
            color: '#5b8ff9',
          },
        },
        {
          name: t('ViewDashboard.取消订单量'),
          itemStyle: {
            color: '#e84825',
          },
          lineStyle: {
            color: '#e84825',
          },
        },
        {
          name: t('ViewDashboard.退货退款量'),
          itemStyle: {
            color: '#faad14',
          },
          lineStyle: {
            color: '#faad14',
          },
        },
        {
          name: t('ViewDashboard.拒收量'),
          itemStyle: {
            color: '#269a99',
          },
          lineStyle: {
            color: '#269a99',
          },
        },
      ],
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: dateRange,
    },
    yAxis: {
      type: 'value',
    },
    tooltip: {
      trigger: 'axis',
      valueFormatter: (value) => numberAbs(value as number),
    },
    series: [
      {
        data: data.map((x) => x.COD付款订单总量),
        type: 'line',
        smooth: true,
        name: t('ViewDashboard.下单总量'),
        areaStyle: {
          color: '#5b8ff9',
          opacity: 0.25,
        },
        lineStyle: {
          color: '#5b8ff9',
        },
        itemStyle: {
          color: '#5b8ff9',
        },
      },
      {
        data: data.map((x) => numberNegative(x.COD取消订单量)),
        type: 'line',
        smooth: true,
        name: t('ViewDashboard.取消订单量'),
        areaStyle: {
          color: '#e84825',
          opacity: 0.25,
        },
        lineStyle: {
          color: '#e84825',
        },
        itemStyle: {
          color: '#e84825',
        },
      },

      {
        data: data.map((x) => numberNegative(x.COD退货退款量)),
        type: 'line',
        smooth: true,
        name: t('ViewDashboard.退货退款量'),
        areaStyle: {
          color: '#faad14',
          opacity: 0.25,
        },
        lineStyle: {
          color: '#faad14',
        },
        itemStyle: {
          color: '#faad14',
        },
      },
      {
        data: data.map((x) => numberNegative(x.COD拒收量)),
        type: 'line',
        smooth: true,
        areaStyle: {
          color: '#269a99',
          opacity: 0.25,
        },
        lineStyle: {
          color: '#269a99',
        },
        itemStyle: {
          color: '#269a99',
        },
        name: t('ViewDashboard.拒收量'),
      },
    ],
  };
};
const convert2非CODOptions = (value: Dashboard.OrderOverviewInner, t: any): EChartsOption => {
  const data = value.非COD每日盈利 || [];
  const dateRange = data.map((x) => x.date).map((x) => dayjs(x).format('MM-DD'));

  return {
    legend: {
      data: [
        {
          name: t('ViewDashboard.付款订单总量'),
          itemStyle: {
            color: '#5b8ff9',
          },
          lineStyle: {
            color: '#5b8ff9',
          },
        },
        {
          name: t('ViewDashboard.取消订单量'),
          itemStyle: {
            color: '#e84825',
          },
          lineStyle: {
            color: '#e84825',
          },
        },
        {
          name: t('ViewDashboard.退货退款量'),
          itemStyle: {
            color: '#faad14',
          },
          lineStyle: {
            color: '#faad14',
          },
        },
      ],
    },
    grid: {
      left: '6%',
      right: '6%',
    },
    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: dateRange,
    },
    yAxis: {
      type: 'value',
    },
    tooltip: {
      trigger: 'axis',
      valueFormatter: (value) => numberAbs(value as number),
    },
    series: [
      {
        data: data.map((x) => x.非COD付款订单总量),
        type: 'line',
        smooth: true,
        areaStyle: {
          color: '#5b8ff9',
          opacity: 0.25,
        },
        lineStyle: {
          color: '#5b8ff9',
        },
        itemStyle: {
          color: '#5b8ff9',
        },
        name: t('ViewDashboard.付款订单总量'),
      },
      {
        data: data.map((x) => numberNegative(x.非COD取消订单量)),
        type: 'line',
        smooth: true,
        areaStyle: {
          color: '#e84825',
          opacity: 0.25,
        },
        lineStyle: {
          color: '#e84825',
        },
        itemStyle: {
          color: '#e84825',
        },
        name: t('ViewDashboard.取消订单量'),
      },
      {
        data: data.map((x) => numberNegative(x.非COD退货退款量)),
        type: 'line',
        smooth: true,
        areaStyle: {
          color: '#faad14',
          opacity: 0.25,
        },
        lineStyle: {
          color: '#faad14',
        },
        itemStyle: {
          color: '#faad14',
        },
        name: t('ViewDashboard.退货退款量'),
      },
    ],
  };
};

const Profit = (props: Props) => {
  const { value } = props;
  const { t } = useTranslate();
  const { hasTokopedia } = useDashboardFilterContext();
  const [_tab, setTab] = useState<'COD' | '非COD'>('非COD');
  const chartDomRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<echarts.ECharts>();
  const { setTrendValue } = useDrawerContext();
  const { width } = useSize(chartDomRef) || {};

  const {
    platformInfo: { isOnlyMercadoOrOnlyTokopedia },
  } = useDashboardPlatformInfo();

  const tab = isOnlyMercadoOrOnlyTokopedia ? '非COD' : _tab;

  useEffect(() => {
    chartRef.current?.resize();
  }, [width]);
  useEffect(() => {
    if (!value || !chartDomRef.current) {
      return;
    }
    if (!chartRef.current) {
      chartRef.current = initEcharts(chartDomRef.current);
    }
    const options = tab === 'COD' ? convert2CODOptions(value, t) : convert2非CODOptions(value, t);
    chartRef.current.clear();
    chartRef.current.setOption(applyChartTheme(options));
  }, [tab, value, t]);
  useUpdateEffect(() => {
    setTrendValue({
      visible: false,
    });
  }, [tab]);
  const noDataMap = {
    COD: !value?.['COD每日盈利']?.length,
    非COD: !value?.['非COD每日盈利']?.length,
  };

  return (
    <Container>
      <Tabs
        onChange={(key) => {
          setTab(key as any);
        }}
        activeKey={tab}
        animated={false}
      >
        <Tabs.TabPane
          tab={
            isOnlyMercadoOrOnlyTokopedia ? (
              t('每日订单变化')
            ) : (
              <div>
                {t('ViewDashboard.非COD每日盈利变化')}
                {hasTokopedia && (
                  <Tooltip title={t('dashboard_tokopedia_tips1')}>
                    <What />
                  </Tooltip>
                )}
              </div>
            )
          }
          key="非COD"
        />
        {!isOnlyMercadoOrOnlyTokopedia && (
          <Tabs.TabPane
            tab={
              <div>
                {t('ViewDashboard.COD每日盈利变化')}
                {hasTokopedia && (
                  <Tooltip title={t('dashboard_tokopedia_tips1')}>
                    <What />
                  </Tooltip>
                )}
              </div>
            }
            key="COD"
          />
        )}
      </Tabs>
      <Relative>
        <div className="el" ref={chartDomRef} />
        <div className="chart">{noDataMap[tab] ? <Empty /> : null}</div>
      </Relative>
    </Container>
  );
};

export default Profit;
